import { queryOptions } from '@tanstack/react-query';
import {
    BondQuantityType,
    Order,
    OrderFillType,
    OrderMarketTime,
    OrderTransaction,
    OrderType,
    OrderValidity,
    PriceType,
    Quote,
} from '@orbis-systems/orbis-api-middleware';
import { SuccessClientResponse } from '@orbis-systems/orbis-api-clients';
import { OrderFilter } from '@/constants/lookups';
import { orbis } from '../api';

export type OrdersListingForBranchResponse = SuccessClientResponse<Order[]>;
export type OrdersListingForModelResponse = SuccessClientResponse<Order[]>;
export type OrdersListingResponse = SuccessClientResponse<Order[]>;

export interface OrdersListingForBranchQueryParams {
    type: string;
    query?: {
        /** Return bond pricing type in `Percentage|Decimal` (optional) */
        bondPriceType?: PriceType;
        loadQuotes?: boolean;
        symbol?: string;
        bondQuantityType?: BondQuantityType;
    };
}

export interface OrdersListingForModelQueryParams {
    modelId: string;
    type: OrderFilter;
    query?: {
        loadQuotes?: boolean;
    };
}

export interface OrdersListingQueryParams {
    type: OrderFilter;
    query?: {
        loadQuotes?: boolean;
        /** Account ID (optional) */
        accountId?: number;
        /** Account number (optional) */
        account?: string;
        /** Ticker symbol (optional) */
        symbol?: string;
    };
}

export const ordersQueries = {
    all: () => queryOptions({ queryKey: ['orders'] }),
    listingForBranch: (params: OrdersListingForBranchQueryParams) =>
        queryOptions<OrdersListingForBranchResponse>({
            queryKey: [...ordersQueries.all().queryKey, 'listingForBranch', params],
            queryFn: () => orbis.orders.listingForBranch(params.type, params.query),
        }),
    listingForModel: (params: OrdersListingForModelQueryParams) =>
        queryOptions<OrdersListingForModelResponse>({
            queryKey: [...ordersQueries.all().queryKey, 'listingForModel', params],
            queryFn: () => orbis.orders.listingForModel(params.modelId, params.type, params.query),
        }),
    listing: (params: OrdersListingQueryParams) =>
        queryOptions<OrdersListingResponse>({
            queryKey: [...ordersQueries.all().queryKey, 'listing', params],
            queryFn: () => orbis.orders.listing(params.type, params.query),
        }),
};

export interface BasketPreviewEquityParams {
    account: {
        accountId: number;
    };
    orders: {
        quote: {
            symbol: string;
        };
        transaction: OrderTransaction.Buy | OrderTransaction.Sell;
        marketTime: OrderMarketTime.PreMarket | OrderMarketTime.PostMarket | OrderMarketTime.CoreMarket;
        orderType: OrderType.Market | OrderType.Limit;
        fillType: OrderFillType.AllOrNone | OrderFillType.Any;
        validity: OrderValidity.GoodForDay;
        quantity: number;
        limitPrice?: number;
    }[];
}

export type BasketPreviewEquityResponse = SuccessClientResponse<Quote[]>;
export type BasketPlacementEquityResponse = SuccessClientResponse<{ basketRef: string; orderReferences: string[] }>;

export const ordersMutations = {
    basketPreviewEquity: (params: BasketPreviewEquityParams): Promise<BasketPreviewEquityResponse> =>
        orbis.orders.v2.equities.basketPreview(params),
    basketPlacement: (params: BasketPreviewEquityParams): Promise<BasketPlacementEquityResponse> =>
        orbis.orders.v2.equities.basketPlacement(params),
};
